import { stamp, tzAbbr } from "./util";

export const loadImg = (src) =>
  new Promise((res) => {
    if (!src) return res(null);
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => res(null);
    i.src = src;
  });

export const loadAssets = async (profile) => ({ sig: await loadImg(profile.signature), logo: await loadImg(profile.logo) });

export function creatorText(profile) {
  const parts = [];
  if (profile.stamp.name && profile.fullName) parts.push(profile.fullName);
  if (profile.stamp.initials && profile.initials) parts.push(profile.initials);
  return parts.join(" • ");
}

export function creatorStamp(profile) {
  const parts = [];
  if (profile.fullName) parts.push(profile.fullName);
  if (profile.initials) parts.push(`(${profile.initials})`);
  if (profile.signature) parts.push("• e-signature on file");
  return parts.join(" ");
}

export function drawWatermark(ctx, w, h, profile, assets, bottom = 0) {
  const unit = Math.min(w, h);
  const text = creatorText(profile);
  ctx.save();
  ctx.globalAlpha = profile.opacity;
  if (text) {
    ctx.save();
    ctx.translate(w / 2, (h - bottom) / 2);
    ctx.rotate(-Math.PI / 8);
    ctx.font = `700 ${Math.round(unit * 0.085)}px Outfit, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = Math.max(1, unit * 0.004);
    ctx.strokeStyle = "rgba(0,0,0,0.7)";
    ctx.fillStyle = "#ffffff";
    ctx.strokeText(text, 0, 0, w * 0.9);
    ctx.fillText(text, 0, 0, w * 0.9);
    ctx.restore();
  }
  const m = unit * 0.03;
  let y = h - bottom - m;
  ctx.shadowColor = "rgba(255,255,255,0.9)";
  ctx.shadowBlur = unit * 0.01;
  if (profile.stamp.signature && assets.sig) {
    const sw = unit * 0.3, sh = (sw * assets.sig.height) / assets.sig.width;
    ctx.drawImage(assets.sig, w - m - sw, y - sh, sw, sh);
    y -= sh + m / 2;
  }
  if (profile.stamp.logo && assets.logo) {
    const lw = unit * 0.14, lh = (lw * assets.logo.height) / assets.logo.width;
    ctx.drawImage(assets.logo, w - m - lw, y - lh, lw, lh);
  }
  ctx.restore();
}

export function drawPixelBar(ctx, w, h, { left1, left2, right1, right2 }) {
  const bh = Math.max(56, Math.round(h * 0.09));
  const pad = bh * 0.3, fs = Math.round(bh * 0.28);
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = "rgba(10,10,12,0.8)";
  ctx.fillRect(0, h - bh, w, bh);
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff";
  ctx.font = `600 ${fs}px "JetBrains Mono", monospace`;
  ctx.fillText(left1, pad, h - bh + bh * 0.44, w * 0.56 - pad);
  ctx.font = `400 ${Math.round(fs * 0.82)}px "DM Sans", sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText(left2, pad, h - bh + bh * 0.8, w * 0.56 - pad);
  ctx.textAlign = "right";
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${fs}px Outfit, sans-serif`;
  ctx.fillText(right1, w - pad, h - bh + bh * 0.44, w * 0.4 - pad);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = `400 ${Math.round(fs * 0.82)}px "DM Sans", sans-serif`;
  ctx.fillText(right2, w - pad, h - bh + bh * 0.8, w * 0.4 - pad);
  ctx.restore();
  return bh;
}

export function barLines({ date, title, ref, profile, prefix = "" }) {
  const d = new Date(date);
  return {
    left1: `${prefix}${stamp(d)} ${tzAbbr(d)}`,
    left2: `${title || "Note"}${ref ? ` • Ref ${ref}` : ""}`,
    right1: "★ StarVault • by Sammy Star",
    right2: (profile.apply.media && creatorText(profile)) || "",
  };
}

export async function decodeImage(blob) {
  try {
    return await createImageBitmap(blob);
  } catch {
    const url = URL.createObjectURL(blob);
    try {
      const img = await loadImg(url);
      if (!img) throw new Error("Unsupported image format");
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

const toBlob = (c, type = "image/jpeg", q = 0.92) => new Promise((r) => c.toBlob(r, type, q));

function stampCanvas(src, w, h, ctx) {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const g = c.getContext("2d");
  g.drawImage(src, 0, 0, w, h);
  const bh = Math.max(56, Math.round(h * 0.09));
  if (ctx.profile.apply.media) drawWatermark(g, w, h, ctx.profile, ctx.assets, bh);
  drawPixelBar(g, w, h, barLines(ctx));
  return c;
}

export async function stampPhoto(blob, ctx) {
  try {
    const bmp = await decodeImage(blob);
    return await toBlob(stampCanvas(bmp, bmp.width, bmp.height, ctx));
  } catch {
    return null;
  }
}

export const timecode = (t) => {
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = (t % 60).toFixed(2);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${s.padStart(5, "0")}`;
};

export async function frameFromVideo(video, ctx) {
  const c = stampCanvas(video, video.videoWidth, video.videoHeight, {
    ...ctx, prefix: `TC ${timecode(video.currentTime)} • `,
  });
  return toBlob(c);
}

export async function videoThumb(blob, ctx) {
  const url = URL.createObjectURL(blob);
  const v = document.createElement("video");
  v.muted = true; v.playsInline = true; v.preload = "auto"; v.src = url;
  try {
    await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = rej; setTimeout(rej, 15000); });
    v.currentTime = Math.min(1, (v.duration || 0) / 2);
    await new Promise((res) => { v.onseeked = res; setTimeout(res, 5000); });
    if (!v.videoWidth) return null;
    return await frameFromVideo(v, ctx);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const LEVELS = {
  original: { label: "100% (Original)", scale: 1, q: 0.92 },
  standard: { label: "65% (Standard)", scale: 0.65, q: 0.68 },
  compact: { label: "25% (Compact)", scale: 0.25, q: 0.5 },
};

export async function compressImage(blob, level = "original", maxDim = 4000) {
  const bmp = await decodeImage(blob);
  const { scale, q } = LEVELS[level];
  let w = bmp.width * scale, h = bmp.height * scale;
  const r = Math.min(1, maxDim / Math.max(w, h));
  w = Math.round(w * r); h = Math.round(h * r);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const g = c.getContext("2d");
  g.fillStyle = "#fff"; g.fillRect(0, 0, w, h);
  g.drawImage(bmp, 0, 0, w, h);
  return { blob: await toBlob(c, "image/jpeg", q), w, h };
}
